import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Container from "../../../components/Container";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";

/** 날짜 포맷터 (YYYY-MM-DD HH:mm) */
const fmtDateTime = (d) => {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
};

export default function InquiryDetail() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // 본문 데이터
  const [post, setPost] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  // 비밀번호 모달
  const [pwOpen, setPwOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [verr, setVerr] = useState("");
  const [verifying, setVerifying] = useState(false);

  // 글 비밀번호를 댓글에서도 쓰기 위한 보관(작성자용)
  const authorPwRef = useRef("");

  // 로그인 사용자 권한(관리자 여부)
  const [amAdmin, setAmAdmin] = useState(false);

  // ===== Comments 시작 =====
  const [comments, setComments] = useState([]);
  const [cLoading, setCLoading] = useState(true);
  const [cError, setCError] = useState("");
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState("");
  const [editingSubmitting, setEditingSubmitting] = useState(false);

  const loadComments = async () => {
    setCLoading(true);
    setCError("");
    try {
      const res = await fetch(`/api/inquiry/detail/${id}/comments`);
      if (!res.ok) throw new Error("HTTP " + res.status);
      const api = await res.json();
      setComments(Array.isArray(api.data) ? api.data : []);
    } catch (e) {
      console.error(e);
      setCError(t("inquiryDetail.cmt.loadFail"));
      setComments([]);
    } finally {
      setCLoading(false);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      const body = {
        content: newComment.trim(),
        role: amAdmin ? "ADMIN" : "AUTHOR",
      };
      if (!amAdmin)
        body.password =
          authorPwRef.current || prompt(t("inquiryDetail.cmt.askPw"));
      if (!amAdmin && !body.password) {
        setSubmittingComment(false);
        return;
      }
      const res = await fetch(`/api/inquiry/detail/${id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      await res.json();
      setNewComment("");
      await loadComments();
    } catch (e) {
      console.error(e);
      alert(t("inquiryDetail.cmt.addFail"));
    } finally {
      setSubmittingComment(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.commentId);
    setEditingContent(c.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingContent("");
  };

  const saveEdit = async (commentId) => {
    if (!editingContent.trim()) return;
    setEditingSubmitting(true);
    try {
      const body = { content: editingContent.trim() };
      if (!amAdmin)
        body.password =
          authorPwRef.current || prompt(t("inquiryDetail.cmt.askPw"));
      if (!amAdmin && !body.password) {
        setEditingSubmitting(false);
        return;
      }
      const res = await fetch(
        `/api/inquiry/detail/${id}/comments/${commentId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) throw new Error("HTTP " + res.status);
      await res.json();
      setEditingId(null);
      setEditingContent("");
      await loadComments();
    } catch (e) {
      console.error(e);
      alert(t("inquiryDetail.cmt.editFail"));
    } finally {
      setEditingSubmitting(false);
    }
  };

  const removeComment = async (commentId) => {
    const ok = window.confirm(t("inquiryDetail.cmt.confirmDelete"));
    if (!ok) return;
    try {
      const body = {};
      if (!amAdmin) {
        body.password =
          authorPwRef.current || prompt(t("inquiryDetail.cmt.askPw"));
        if (!body.password) return;
      }
      const res = await fetch(
        `/api/inquiry/detail/${id}/comments/${commentId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) throw new Error("HTTP " + res.status);
      await res.json();
      await loadComments();
    } catch (e) {
      console.error(e);
      alert(t("inquiryDetail.cmt.deleteFail"));
    }
  };
  // ===== Comments 끝 =====

  // 로그인/관리자 여부 확인
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch("/api/user/status", {
          credentials: "include", // 쿠키(access_token) 같이 보내기
        });
        if (!res.ok) return;
        const body = await res.json();
        setAmAdmin(body.isAdmin === true);
      } catch (e) {
        console.error(e);
      }
    };
    fetchStatus();
  }, []);

  // 최초 로드(본문)
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/inquiry/detail/${id}`);
        if (!res.ok) throw new Error("HTTP " + res.status);

        const api = await res.json();
        setPost(api.data);
        setAuthorized(!api.data.secret);
      } catch (e) {
        console.error(e);
        setPost(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // ✅ 관리자일 경우 비밀글도 자동 열람 허용
  useEffect(() => {
    if (post?.secret && amAdmin && !authorized) {
      setAuthorized(true);
    }
  }, [post, amAdmin, authorized]);

  // 댓글 목록 로드 (비밀글 + 미인증이면 로드하지 않음)
  useEffect(() => {
    if (!post) return;

    if (post.secret && !authorized) {
      // 비밀번호 인증 전에는 댓글을 아예 로드하지 않고, 상태 초기화
      setComments([]);
      setCError("");
      setCLoading(false);
      return;
    }

    // 공개 글이거나, 비밀글이지만 인증된 경우만 로드
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, post, authorized]);

  // 목록에서 비밀글 클릭 시 state로 넘어온 플래그가 있으면 자동 모달
  useEffect(() => {
    if (!loading && post?.secret && !authorized) {
      if (location.state?.promptPassword) setPwOpen(true);
    }
  }, [loading, post, authorized, location.state]);

  const onVerify = async (e) => {
    e.preventDefault();
    setVerr("");

    if (!password.trim()) {
      setVerr(t("inquiryDetail.verify.need"));
      return;
    }

    setVerifying(true);
    try {
      const res = await fetch(`/api/inquiry/detail/${id}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);

      const api = await res.json();
      if (!api.data.ok) throw new Error("INVALID");

      setAuthorized(true);
      setPwOpen(false);
      authorPwRef.current = password.trim(); // 작성자 댓글용으로 저장
      setPassword("");
      setVerr("");
    } catch (err) {
      console.error(err);
      setVerr(t("inquiryDetail.verify.fail"));
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    const ok = window.confirm(t("inquiryDetail.confirmDelete"));
    if (!ok) return;

    try {
      const body = {};
      // ✅ 관리자면 비밀번호 패스
      if (!amAdmin) {
        const pw = window.prompt(t("inquiryDetail.askPassword"));
        if (!pw) return;
        body.password = pw;
      }

      const res = await fetch(`/api/inquiry/detail/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      await res.json();
      nav("/inquiry", { replace: true });
    } catch (err) {
      console.error(err);
      alert(t("inquiryDetail.deleteFail"));
    }
  };

  const handleEdit = () => {
    nav(`/inquiry/edit/${id}`, { state: { post } });
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-slate-500">
        {t("inquiryDetail.loading")}
      </div>
    );
  }

  if (!post) {
    return (
      <div className="py-16 text-center text-sm text-slate-500">
        {t("inquiryDetail.notFound")}
      </div>
    );
  }

  return (
    <div className="text-blue-900">
      {/* 헤더 */}
      <section className="py-10 md:py-14">
        <Container>
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <h1 className="text-xl font-bold md:text-2xl">
                {post.secret && !authorized
                  ? t("inquiryDetail.secretTitle")
                  : post.title}
              </h1>
              <div className="text-sm text-slate-600">
                <span className="mr-3">{post.author}</span>
                <span>{fmtDateTime(post.createdAt)}</span>
              </div>
            </div>

            {/* 메타 뱃지 */}
            <div className="mt-2 flex flex-wrap gap-2">
              {post.secret && (
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200">
                  <Lock className="h-3.5 w-3.5" />
                  {t("inquiryDetail.secret")}
                </span>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* 본문 */}
      <section className="pb-10">
        <Container>
          {post.secret && !authorized ? (
            <div className="rounded-xl border bg-white p-6 text-sm shadow-sm">
              <p className="text-slate-700">{t("inquiryDetail.secretGuide")}</p>
              <div className="mt-4">
                <button
                  onClick={() => setPwOpen(true)}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  {t("inquiryDetail.openPassword")}
                </button>
                <button
                  onClick={() => nav(-1)}
                  className="ml-2 rounded-lg px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  {t("common.back")}
                </button>
              </div>
            </div>
          ) : (
            <>
              <article className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="prose prose-slate max-w-none text-[15px] leading-7">
                  <p style={{ whiteSpace: "pre-wrap" }}>{post.content}</p>
                </div>
              </article>

              {/* 하단 버튼 */}
              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  onClick={() => nav(-1)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  {t("common.back")}
                </button>
                <button
                  onClick={handleEdit}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  {t("common.edit")}
                </button>
                <button
                  onClick={handleDelete}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  {t("common.delete")}
                </button>
              </div>
            </>
          )}
        </Container>
      </section>

      {/* ===== 댓글 영역 ===== */}
      <section className="pb-16">
        <Container>
          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">
              {t("inquiryDetail.cmt.title")}
            </div>

            {/* 목록 */}
            {post.secret && !authorized ? (
              <p className="mt-3 text-sm text-slate-500">
                {t("inquiryDetail.cmt.needPassword")}
              </p>
            ) : cLoading ? (
              <p className="mt-3 text-sm text-slate-500">
                {t("inquiryDetail.cmt.loading")}
              </p>
            ) : cError ? (
              <p className="mt-3 text-sm text-red-600">{cError}</p>
            ) : comments.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">
                {t("inquiryDetail.cmt.empty")}
              </p>
            ) : (
              <ul className="mt-3 space-y-3">
                {comments.map((c) => (
                  <li key={c.commentId} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-600">
                        <span className="mr-2 font-medium">
                          {c.role === "ADMIN"
                            ? t("inquiryDetail.cmt.admin")
                            : t("inquiryDetail.cmt.author")}
                        </span>
                        <span className="text-xs text-slate-500">
                          {fmtDateTime(c.createdAt)}
                        </span>
                        {c.updatedAt && (
                          <span className="ml-2 text-xs text-slate-400">
                            {t("inquiryDetail.cmt.edited")}{" "}
                            {fmtDateTime(c.updatedAt)}
                          </span>
                        )}
                      </div>

                      {/* {(amAdmin || c.mine) && (
                        <div className="flex gap-2">
                          {editingId === c.commentId ? (
                            <>
                              <button
                                className="rounded-md px-2 py-1 text-xs ring-1 ring-slate-200 hover:bg-slate-50"
                                onClick={() => saveEdit(c.commentId)}
                                disabled={editingSubmitting}
                              >
                                {t("common.save")}
                              </button>
                              <button
                                className="rounded-md px-2 py-1 text-xs ring-1 ring-slate-200 hover:bg-slate-50"
                                onClick={cancelEdit}
                                disabled={editingSubmitting}
                              >
                                {t("common.cancel")}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="rounded-md px-2 py-1 text-xs ring-1 ring-slate-200 hover:bg-slate-50"
                                onClick={() => startEdit(c)}
                              >
                                {t("common.edit")}
                              </button>
                              <button
                                className="rounded-md bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                                onClick={() => removeComment(c.commentId)}
                              >
                                {t("common.delete")}
                              </button>
                            </>
                          )}
                        </div>
                      )} */}
                    </div>

                    {/* 내용 */}
                    <div className="mt-2 text-sm text-slate-800">
                      {editingId === c.commentId ? (
                        <textarea
                          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                          rows={3}
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                        />
                      ) : (
                        <p style={{ whiteSpace: "pre-wrap" }}>{c.content}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* 입력 */}
            {!post.secret || authorized ? (
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-800">
                  {t("inquiryDetail.cmt.write")}
                </label>
                <textarea
                  rows={3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t("inquiryDetail.cmt.ph")}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={addComment}
                    disabled={submittingComment}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {submittingComment
                      ? t("inquiryDetail.cmt.submitting")
                      : t("inquiryDetail.cmt.submit")}
                  </button>
                </div>
                {!amAdmin && (
                  <p className="mt-2 text-xs text-slate-500">
                    {t("inquiryDetail.cmt.note")}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </Container>
      </section>

      {/* 비밀번호 모달 (비공개 글 열람용) */}
      {pwOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setPwOpen(false)}
          />
          <div className="absolute inset-x-4 top-24 mx-auto max-w-md rounded-2xl border bg-white p-5 shadow-2xl">
            <div className="flex items-center gap-2 text-slate-900">
              <Lock className="h-5 w-5 text-slate-700" />
              <div className="text-sm font-semibold">
                {t("inquiryDetail.verify.title")}
              </div>
            </div>

            <form onSubmit={onVerify} className="mt-3 space-y-3">
              <p className="text-xs text-slate-500">
                {t("inquiryDetail.verify.desc")}
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-700">
                  {t("inquiryDetail.verify.password")}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("inquiryDetail.verify.phPassword")}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {verr && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {verr}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setPwOpen(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={verifying}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {verifying
                    ? t("inquiryDetail.verify.submitting")
                    : t("inquiryDetail.verify.submit")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
